/**
 * Single-source-of-truth config for Lore billing, modeled after the
 * ShipFast convention: one default-exported `config` object with a
 * `stripe.plans` array that holds everything the app needs — display
 * copy, dollar pricing, Stripe price IDs, feature lists for the pricing cards, and
 * Lore's entitlement flags for server-side gating.
 *
 * Adding, removing, or retuning a plan is a one-file edit. Price IDs
 * may come from env vars when they are minted outside source control.
 */

import { MILLICENTS_PER_DOLLAR, getEconomics } from './economics.ts';
import { STRIPE_PRICE_ENV_VARS } from './catalog.ts';

export const PLAN_TIERS = ['free', 'team'] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/** Binding scope for a paid plan. */
export type PlanSubjectType = 'user' | 'organization';

/**
 * Machine-readable feature flags used by server-side gating (review
 * access). Kept separate from the human-readable `features` array below
 * so plan copy can evolve without changing gate behavior.
 *
 * Workspace-scoped thread sharing is intentionally NOT an entitlement:
 * it's available to anyone who belongs to a workspace, gated only on
 * workspace membership, not on plan tier.
 */
export type PlanEntitlements = {
  review: boolean;
};

export type RegisteredStripePriceEnvVar =
  (typeof STRIPE_PRICE_ENV_VARS)[keyof typeof STRIPE_PRICE_ENV_VARS];
export type EnvPriceIdReference = `env.${RegisteredStripePriceEnvVar}`;
export type StripePriceId = `price_${string}`;

export type Plan = {
  /** Short machine id used everywhere a plan is referenced. */
  tier: PlanTier;
  /** What we bill against — a user or an organization (Team). */
  subjectType: PlanSubjectType;
  /** Display name on the pricing card. */
  name: string;
  /** One-line pitch shown under the name. */
  description: string;
  /**
   * Dollar price shown to customers (for seat-priced plans, this is
   * the per-seat monthly charge). 0 for the free tier. Matches the
   * number Stripe charges, stored here to keep the pricing page
   * fully driven by config.
   */
  price: number;
  /**
   * Optional crossed-out "was" price for promos. Omit to render a
   * single price.
   */
  priceAnchor?: number;
  /**
   * Stripe price id, or an `env.VAR_NAME` reference resolved by
   * `resolveStripePriceId`. `null` for free.
   */
  priceId: StripePriceId | EnvPriceIdReference | null;
  /**
   * Highlight this card on the pricing page (border, accent). Exactly
   * one plan should set this; defaults to the Team tier.
   */
  isFeatured?: boolean;
  /**
   * Minimum seat count for per-seat plans. Omit for flat plans; the
   * server clamps checkout + seat-update to this floor.
   */
  minSeats?: number;
  /** Bullet list rendered on the pricing card. */
  features: { name: string }[];
  /** Gating flags consumed by the API (review access). */
  entitlements: PlanEntitlements;
};

export type BillingConfig = {
  stripe: {
    plans: readonly Plan[];
  };
};

/**
 * Root config object. Keep this the only place that mentions plan
 * names, prices, price ids, or feature copy. Everything else should
 * import from `@lore/billing` and work through `getPlan(tier)` /
 * `meetsPlan(...)` / etc.
 */
const config: BillingConfig = {
  stripe: {
    plans: [
      {
        tier: 'free',
        subjectType: 'user',
        name: 'Free',
        description:
          'Share any thread from Claude Code, Codex, or Cowork. Share with friends or keep private. Share skills with your team instantly.',
        price: 0,
        priceId: null,
        features: [
          { name: 'Share threads from Claude Code, Codex, and Cowork' },
          { name: 'Share with friends or keep private' },
          { name: 'Skills tab — tool usage across every thread' },
          { name: 'Share skills with your team instantly' },
        ],
        entitlements: {
          review: false,
        },
      },
      {
        tier: 'team',
        subjectType: 'organization',
        name: 'Team',
        description:
          'Org-wide thread sharing plus the review surface. Each seat grants a pooled $20/mo of AI credits.',
        isFeatured: true,
        // Derived from the single source of truth in economics.ts so the
        // displayed list price can never drift from what we actually charge
        // and grant. $20/seat today; each seat also grants $20 of pooled,
        // period-expiring AI credits (grantPerSeatMillicents).
        price: getEconomics().seatPriceMillicents / MILLICENTS_PER_DOLLAR,
        minSeats: 2,
        // The $20 Team seat price is supplied exclusively via env in every
        // environment. Do not hard-code a test or live fallback here: a missing
        // or stale fallback can silently charge the wrong amount. The env
        // reference fails closed (checkout returns billing-unavailable) when
        // STRIPE_PRICE_TEAM_SEAT_MONTHLY is unset.
        priceId: `env.${STRIPE_PRICE_ENV_VARS.teamSeatPriceId}`,
        features: [
          { name: 'Everything in Free' },
          { name: 'Workspace-wide thread sharing' },
          { name: 'Minimum 2 seats' },
          { name: 'Review feature (beta)' },
        ],
        entitlements: {
          review: true,
        },
      },
    ],
  },
};

export default config;

// ---- Helpers -------------------------------------------------------
// Everything below is a thin accessor so the rest of the codebase
// never reaches into the raw config and plan shape churn stays local.

/** Admin-override enum — matches the DB check constraint. */
export const PLAN_ADMIN_OVERRIDE_VALUES = PLAN_TIERS;

export const PLANS: readonly Plan[] = config.stripe.plans;

const planByTier: Readonly<Record<PlanTier, Plan>> = Object.freeze(
  Object.fromEntries(PLANS.map((plan) => [plan.tier, plan])) as Record<
    PlanTier,
    Plan
  >,
);

const PLAN_RANK: Record<PlanTier, number> = {
  free: 0,
  team: 1,
};

export const meetsPlan = (tier: PlanTier, minimum: PlanTier): boolean =>
  PLAN_RANK[tier] >= PLAN_RANK[minimum];

export const getPlan = (tier: PlanTier): Plan => planByTier[tier];

export const getEntitlements = (tier: PlanTier): PlanEntitlements =>
  planByTier[tier].entitlements;

/**
 * Back-compat alias — earlier code called this `getFeatures`. The
 * returned shape is `PlanEntitlements`; kept to avoid churn in
 * handler sites that already import the older name.
 */
export const getFeatures = getEntitlements;

/**
 * Resolve a Stripe price id for a paid plan. Config values of the form
 * `env.VAR_NAME` are dereferenced from the provided env map and return null
 * when unset, so env-only plans fail closed. Team also accepts its
 * STRIPE_PRICE_TEAM_SEAT_MONTHLY env override before falling back to config.
 */
export const resolveStripePriceId = (
  tier: PlanTier,
  env: Record<string, string | undefined> = typeof process !== 'undefined'
    ? (process.env ?? {})
    : {},
): string | null => {
  const legacyEnvKey =
    tier === 'team' ? 'STRIPE_PRICE_TEAM_SEAT_MONTHLY' : null;
  if (legacyEnvKey) {
    const override = env[legacyEnvKey];
    if (override && override.length > 0) return override;
  }
  return resolveConfiguredPriceId(planByTier[tier].priceId, env);
};

const resolveConfiguredPriceId = (
  priceId: Plan['priceId'],
  env: Record<string, string | undefined>,
): string | null => {
  if (!priceId) return null;
  if (priceId.startsWith('env.')) {
    const envValue = env[priceId.slice('env.'.length)];
    return envValue && envValue.length > 0 ? envValue : null;
  }
  return priceId;
};

/**
 * Clamp a requested seat count to the plan's minimum. Returns the
 * corrected value and a reason string so the UI can surface why we
 * bumped them up ("Team plan requires at least 2 seats.").
 */
export const clampSeatQuantity = (
  tier: PlanTier,
  requested: number,
): { quantity: number; adjusted: boolean; reason: string | null } => {
  const plan = planByTier[tier];
  if (!plan.minSeats) {
    return { quantity: 1, adjusted: requested !== 1, reason: null };
  }
  if (requested < plan.minSeats) {
    return {
      quantity: plan.minSeats,
      adjusted: true,
      reason: `The ${plan.name} plan requires at least ${plan.minSeats} seats.`,
    };
  }
  return { quantity: requested, adjusted: false, reason: null };
};
